
import React from 'react';

export const LegalDocumentTerms: React.FC = () => {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Essential Legal Document Terms
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Understanding legal terminology in apartment building transactions protects your interests and ensures 
            smooth closings. These documents form the foundation of property ownership transfer.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Deed Types and Ownership</h3>
              
              <div className="space-y-6">
                <div className="bg-green-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-green-800 mb-3">Warranty Deed</h4>
                  <p className="text-gray-700 mb-3">
                    Provides the highest level of protection for buyers. The seller guarantees clear title and 
                    agrees to defend against any future claims on the property.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-green-200">
                    <div className="text-sm">
                      <div className="text-green-700 font-medium">Best Protection:</div>
                      <div className="text-gray-600">Full warranties and guarantees included</div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-yellow-800 mb-3">Quitclaim Deed</h4>
                  <p className="text-gray-700 mb-3">
                    Transfers only the seller's interest in the property without warranties or guarantees. 
                    Commonly used in family transfers but risky for arm's length transactions.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-yellow-200">
                    <div className="text-sm">
                      <div className="text-yellow-700 font-medium">Limited Protection:</div>
                      <div className="text-gray-600">No guarantees about title quality</div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-6 rounded-xl">
                  <h4 className="text-lg font-semibold text-blue-800 mb-3">Special Warranty Deed</h4>
                  <p className="text-gray-700 mb-3">
                    Seller warrants against title defects that occurred during their ownership period only. 
                    More protection than quitclaim but less than full warranty deed.
                  </p>
                  <div className="bg-white p-3 rounded-lg border border-blue-200">
                    <div className="text-sm">
                      <div className="text-blue-700 font-medium">Moderate Protection:</div>
                      <div className="text-gray-600">Warranties for seller's ownership period</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/586c6cd0a8e14f167496200f91e6c0d4.jpg"
              alt="Legal documents and contracts for apartment building transactions"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-8 mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Key Legal Terms in Real Estate Transactions</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Title Insurance</h4>
              <p className="text-gray-600 text-sm mb-3">
                Protects against financial loss from title defects, liens, or ownership disputes. Required by most 
                lenders and highly recommended for all buyers.
              </p>
              <div className="text-xs text-blue-600 font-medium">
                Coverage: Up to property purchase price
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-safe-line text-2xl text-green-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Escrow</h4>
              <p className="text-gray-600 text-sm mb-3">
                Neutral third party holds funds, documents, and instructions until all contract conditions are met. 
                Protects both buyers and sellers during the transaction.
              </p>
              <div className="text-xs text-green-600 font-medium">
                Purpose: Neutral transaction management
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-alert-line text-2xl text-red-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Lien</h4>
              <p className="text-gray-600 text-sm mb-3">
                Legal claim against a property for unpaid debts. Must be resolved before clear title can be transferred. 
                Common types include mortgages, tax liens, and mechanic's liens.
              </p>
              <div className="text-xs text-red-600 font-medium">
                Impact: Blocks clear title transfer
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hand-coin-line text-2xl text-yellow-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Earnest Money Deposit</h4>
              <p className="text-gray-600 text-sm mb-3">
                Good faith deposit showing buyer's serious intent to purchase. Held in escrow and applied to purchase 
                price at closing or returned if contract conditions aren't met.
              </p>
              <div className="text-xs text-yellow-600 font-medium">
                Typical Amount: 1-3% of purchase price
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-question-line text-2xl text-purple-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Contingency</h4>
              <p className="text-gray-600 text-sm mb-3">
                Conditions that must be satisfied for the contract to proceed. Common contingencies include financing, 
                inspection, and appraisal. Protects buyers from unforeseen issues.
              </p>
              <div className="text-xs text-purple-600 font-medium">
                Types: Financing, inspection, appraisal
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-exchange-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Transfer Taxes</h4>
              <p className="text-gray-600 text-sm mb-3">
                Government taxes imposed on property transfers. Rates vary by location and may be paid by buyer, 
                seller, or split between parties based on local custom.
              </p>
              <div className="text-xs text-teal-600 font-medium">
                Varies: 0% to 2%+ of sale price
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
          <div>
            <img 
              src="https://readdy.ai/api/search-image?query=earnest%20money%20deposit%20and%20escrow%20process%20for%20apartment%20building%20purchase%2C%20real%20estate%20contract%20signing%20and%20legal%20protection%2C%20professional%20transaction%20management%20and%20document%20security&width=600&height=400&seq=earnest-money-escrow&orientation=landscape"
              alt="Earnest money deposit and escrow process for real estate transactions"
              className="w-full h-80 object-cover rounded-2xl shadow-lg"
            />
          </div>

          <div>
            <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Contract Protection Strategies</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-eye-line text-green-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Due Diligence Period</h4>
                    <p className="text-gray-600 text-sm">
                      Negotiate adequate time (typically 30-60 days) to inspect the property, review financials, 
                      and verify all representations before commitment becomes binding.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-shield-line text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Representation & Warranties</h4>
                    <p className="text-gray-600 text-sm">
                      Ensure seller provides comprehensive warranties about property condition, financial performance, 
                      legal compliance, and environmental status.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-hand-heart-line text-yellow-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Indemnification Clauses</h4>
                    <p className="text-gray-600 text-sm">
                      Include provisions protecting against unknown liabilities, environmental issues, 
                      and legal problems that pre-date your ownership.
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-4 mt-1">
                    <i className="ri-calendar-check-line text-purple-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Closing Timeline</h4>
                    <p className="text-gray-600 text-sm">
                      Build in realistic timelines for financing, inspections, and document preparation. 
                      Include extension provisions for circumstances beyond your control.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-teal-50 p-4 rounded-lg">
                <h5 className="font-semibold text-teal-800 mb-2">Professional Legal Advice</h5>
                <p className="text-teal-700 text-sm">
                  Complex apartment building transactions require experienced real estate attorneys who understand 
                  commercial property law, financing structures, and local regulations.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Legal Protection Is Worth the Investment</h3>
            <p className="text-lg opacity-90 leading-relaxed mb-4">
              Understanding legal terms and working with qualified professionals protects your investment and ensures 
              smooth transactions. The cost of legal services is minimal compared to potential losses from inadequate 
              documentation or missed legal requirements.
            </p>
            <div className="text-sm opacity-75">
              Always consult with experienced real estate attorneys for apartment building transactions. Generic forms 
              and online templates are insufficient for complex commercial real estate deals.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
